import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faServer, faUsers, faChevronDown, faChevronRight, faCog, faBolt, faPencilAlt } from '@fortawesome/free-solid-svg-icons';

interface UserWorkerDto {
    workerName: string;
    hostingCost: number;
    machine: string;
    power: number;
}

interface UserDto {
    id?: number;
    userName?: string;
    email?: string;
    firstName?: string;
    lastName?: string;
    name?: string;
    // Add other user properties as needed - making all optional to handle different API responses
}

interface UserHostingCostUpdateDto {
    userName: string;
    hostingCost: number;
}

interface UpdateHostingCostDto {
    workerId: string;
    hostingCost: number;
}

const HostingCost: React.FC = () => {
    const [users, setUsers] = useState<UserDto[]>([]);
    const [userWorkers, setUserWorkers] = useState<Record<string, UserWorkerDto[]>>({});
    const [expandedUsers, setExpandedUsers] = useState<Set<string>>(new Set());
    const [loadingUsers, setLoadingUsers] = useState<boolean>(true);
    const [loadingWorkers, setLoadingWorkers] = useState<Record<string, boolean>>({});
    const [error, setError] = useState<string>('');
    const [userFilter, setUserFilter] = useState<string>('');

    // Edit mode states
    const [selectedWorkers, setSelectedWorkers] = useState<Record<string, Set<string>>>({}); // userName -> Set of workerNames
    const [bulkCostInput, setBulkCostInput] = useState<string>('');
    const [userCostInput, setUserCostInput] = useState<string>('');
    const [updating, setUpdating] = useState<boolean>(false);

    // Utility function to clean names by removing #cashlab/#CASHLAB
    const cleanName = (name: string): string => {
        if (!name) return name;
        return name.replace(/#cashlab|#CASHLAB/gi, '').trim();
    };

    // Update hosting cost for all workers of a user
    const updateUserHostingCost = async (userName: string, hostingCost: number) => {
        setUpdating(true);
        try {
            const updateRequest: UserHostingCostUpdateDto = {
                userName,
                hostingCost
            };

            await axios.post('/pool/worker/hostingcost/update', [updateRequest]);

            // Refresh worker data for this user
            await fetchUserWorkers(userName);


        } catch (err: any) {
            console.error(`Failed to update hosting cost for user ${userName}:`, err);
            alert('Failed to update hosting cost: ' + (err.response?.data?.message || err.message));
        } finally {
            setUpdating(false);
        }
    };

    // Update hosting cost for individual workers
    const updateWorkerHostingCosts = async (workerUpdates: UpdateHostingCostDto[]) => {
        setUpdating(true);
        try {
            await axios.post('/pool/worker/update/hostingcost', workerUpdates);

            // Refresh worker data for affected users
            const affectedUsers = new Set<string>();
            Object.keys(userWorkers).forEach(userName => {
                const hasAffectedWorker = userWorkers[userName].some(worker =>
                    workerUpdates.some(update => update.workerId === worker.workerName)
                );
                if (hasAffectedWorker) {
                    affectedUsers.add(userName);
                }
            });

            // Refresh data for all affected users
            for (const userName of Array.from(affectedUsers)) {
                await fetchUserWorkers(userName);
            }

            // Clear selections after successful update
            setSelectedWorkers({});

        } catch (err: any) {
            console.error('Failed to update worker hosting costs:', err);
            alert('Failed to update hosting costs: ' + (err.response?.data?.message || err.message));
        } finally {
            setUpdating(false);
        }
    };

    // Toggle worker selection
    const toggleWorkerSelection = (userName: string, workerName: string) => {
        console.log('Toggling worker selection:', { userName, workerName });

        setSelectedWorkers(prev => {
            const newSelected = { ...prev };

            // Initialize the Set for this user if it doesn't exist
            if (!newSelected[userName]) {
                newSelected[userName] = new Set<string>();
            } else {
                // Create a new Set to ensure immutability
                newSelected[userName] = new Set(newSelected[userName]);
            }

            if (newSelected[userName].has(workerName)) {
                console.log('Removing worker from selection');
                newSelected[userName].delete(workerName);
                if (newSelected[userName].size === 0) {
                    delete newSelected[userName];
                }
            } else {
                console.log('Adding worker to selection');
                newSelected[userName].add(workerName);
            }

            console.log('New selected workers state:', newSelected);
            return newSelected;
        });
    };

    // Select all workers for a user
    const selectAllWorkers = (userName: string, workers: UserWorkerDto[]) => {
        setSelectedWorkers(prev => ({
            ...prev,
            [userName]: new Set(workers.map(w => w.workerName))
        }));
    };

    // Clear selected workers for a user
    const clearSelectedWorkers = (userName: string) => {
        setSelectedWorkers(prev => {
            const newSelected = { ...prev };
            delete newSelected[userName];
            return newSelected;
        });
    };

    // Update selected workers with bulk cost
    const updateSelectedWorkers = async (userName: string) => {
        const cost = parseFloat(bulkCostInput);
        if (isNaN(cost) || cost < 0) {
            alert('Please enter a valid hosting cost');
            return;
        }

        const selected = selectedWorkers[userName];
        if (!selected || selected.size === 0) {
            alert('Please select workers to update');
            return;
        }

        const updates: UpdateHostingCostDto[] = Array.from(selected).map(workerName => ({
            workerId: workerName,
            hostingCost: cost
        }));

        await updateWorkerHostingCosts(updates);
        clearSelectedWorkers(userName);
        setBulkCostInput('');
    };

    // Update all workers for a user
    const updateAllWorkersForUser = async (userName: string) => {
        const cost = parseFloat(userCostInput);
        if (isNaN(cost) || cost < 0) {
            alert('Please enter a valid hosting cost');
            return;
        }

        await updateUserHostingCost(userName, cost);
        setUserCostInput('');
    };

    // Get selected worker count for a user
    const getSelectedCount = (userName: string): number => {
        const count = selectedWorkers[userName]?.size || 0;
        console.log(`Selected count for ${userName}:`, count);
        return count;
    };

    // Fetch all users
    const fetchUsers = async () => {
        setLoadingUsers(true);
        setError('');
        try {
            console.log('Fetching users...');
            const response = await axios.get('/pool/admin/users');
            console.log('Users response:', response.data);

            const userData: UserDto[] = Array.isArray(response.data) ? response.data : [];
            console.log('Processed user data:', userData);
            setUsers(userData);
        } catch (err: any) {
            console.error('Error fetching users:', err);
            setError('Failed to fetch users: ' + (err.response?.data?.message || err.message));
        } finally {
            setLoadingUsers(false);
        }
    };

    // Fetch workers for a specific user
    const fetchUserWorkers = async (userName: string) => {
        if (!userName) {
            console.warn('Cannot fetch workers: userName is empty');
            return;
        }

        console.log(`Fetching workers for user: ${userName}`);
        setLoadingWorkers(prev => ({ ...prev, [userName]: true }));

        try {
            const response = await axios.get('/pool/admin/users/workers', {
                params: { username: userName }
            });

            console.log(`Workers response for ${userName}:`, response.data);
            const workerData: UserWorkerDto[] = Array.isArray(response.data) ? response.data : [];
            setUserWorkers(prev => ({ ...prev, [userName]: workerData }));

        } catch (err: any) {
            console.error(`Failed to fetch workers for ${userName}:`, err);
            const errorMessage = err.response?.data?.message || err.message || 'Unknown error';
            console.error(`Error details: ${errorMessage}`);

            // Set empty array on error to prevent undefined issues
            setUserWorkers(prev => ({ ...prev, [userName]: [] }));
        } finally {
            setLoadingWorkers(prev => ({ ...prev, [userName]: false }));
        }
    };

    useEffect(() => {
        fetchUsers();
    }, []);

    // Filter users based on search term
    const filteredUsers = users.filter(user => {
        const userName = user.userName || user.name || user.email?.split('@')[0] || '';
        const email = user.email || '';
        const firstName = user.firstName || '';
        const lastName = user.lastName || '';

        const searchTerm = userFilter.toLowerCase();

        return userName.toLowerCase().includes(searchTerm) ||
               email.toLowerCase().includes(searchTerm) ||
               firstName.toLowerCase().includes(searchTerm) ||
               lastName.toLowerCase().includes(searchTerm);
    });

    // Toggle user expansion and fetch workers if needed
    const toggleUserExpansion = (userName: string) => {
        if (!userName) return;

        const newExpandedUsers = new Set(expandedUsers);
        if (newExpandedUsers.has(userName)) {
            newExpandedUsers.delete(userName);
        } else {
            newExpandedUsers.add(userName);
            // Always fetch workers when expanding (to get fresh data)
            fetchUserWorkers(userName);
        }
        setExpandedUsers(newExpandedUsers);
    };

    // Calculate total hosting cost for a user
    const getUserTotalHostingCost = (workers: UserWorkerDto[]) => {
        if (!Array.isArray(workers)) return 0;
        return workers.reduce((sum, worker) => sum + (worker.hostingCost || 0), 0);
    };

    // Calculate total power consumption for a user
    const getUserTotalPower = (workers: UserWorkerDto[]) => {
        if (!Array.isArray(workers)) return 0;
        return workers.reduce((sum, worker) => sum + (worker.power || 0), 0);
    };

    return (
        <div className="bg-white rounded-lg shadow-lg p-4 sm:p-6">
            <div className="flex items-center gap-2 sm:gap-3 mb-4 sm:mb-6">
                <FontAwesomeIcon icon={faServer} className="w-5 h-5 sm:w-6 sm:h-6 text-blue-600" />
                <h2 className="text-xl sm:text-2xl font-bold text-gray-900">Hosting Cost Management</h2>
            </div>

            {/* User Filter */}
            <div className="bg-gray-50 rounded-lg p-4 mb-6">
                <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                    <h3 className="text-lg font-semibold text-gray-900">Users & Workers</h3>
                    <div className="flex items-center gap-2">
                        <input
                            type="text"
                            placeholder="Search users..."
                            value={userFilter}
                            onChange={(e) => setUserFilter(e.target.value)}
                            className="w-full sm:w-auto px-3 py-2 border border-gray-300 rounded-md shadow-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-blue-500 text-sm"
                        />
                    </div>
                </div>
            </div>

            {/* Error Message */}
            {error && (
                <div className="bg-red-50 border border-red-200 rounded-md p-4 mb-6">
                    <div className="flex">
                        <div className="ml-3">
                            <h3 className="text-sm font-medium text-red-800">Error</h3>
                            <p className="mt-1 text-sm text-red-700">{error}</p>
                        </div>
                    </div>
                </div>
            )}

            {/* Loading State */}
            {loadingUsers && (
                <div className="flex justify-center items-center h-32">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    <span className="ml-3 text-gray-600">Loading users...</span>
                </div>
            )}

            {/* Users Table */}
            {!loadingUsers && filteredUsers.length > 0 && (
                <div className="bg-white rounded-lg border border-gray-200 overflow-hidden">
                    <div className="overflow-x-auto">
                        <table className="min-w-full divide-y divide-gray-200">
                            <thead className="bg-gray-50">
                                <tr>
                                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        User
                                    </th>
                                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Email
                                    </th>
                                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Update Hosting Cost
                                    </th>
                                    <th className="px-3 sm:px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                        Actions
                                    </th>
                                </tr>
                            </thead>
                            <tbody className="bg-white divide-y divide-gray-200">
                                {filteredUsers.map((user) => {
                                    // Use userName field from API response
                                    const userName = user.userName || user.email?.split('@')[0] || `User_${user.id}`;
                                    const workers = userWorkers[userName] || [];
                                    const isExpanded = expandedUsers.has(userName);
                                    const isLoadingWorkers = loadingWorkers[userName] || false;

                                    console.log('Rendering user:', user, 'UserName:', userName);

                                    return (
                                        <React.Fragment key={user.id || userName}>
                                            <tr className="hover:bg-gray-50">
                                                <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                                                    <div className="flex items-center gap-2">
                                                        <button
                                                            onClick={() => toggleUserExpansion(userName)}
                                                            className="text-gray-400 hover:text-gray-600 p-1"
                                                            disabled={!userName}
                                                        >
                                                            <FontAwesomeIcon
                                                                icon={isExpanded ? faChevronDown : faChevronRight}
                                                                className="w-3 h-3"
                                                            />
                                                        </button>
                                                        <div className="truncate max-w-32 sm:max-w-none" title={cleanName(userName)}>
                                                            {cleanName(userName)}
                                                        </div>
                                                    </div>
                                                </td>
                                                <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                                                    <div className="truncate max-w-32 sm:max-w-none" title={user.email || 'No email'}>
                                                        {user.email || 'No email'}
                                                    </div>
                                                </td>
                                                <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    <div className="flex items-center gap-2">
                                                        <span className="text-xs text-gray-600">$</span>
                                                        <input
                                                            type="number"
                                                            step="0.0001"
                                                            min="0"
                                                            value={userCostInput}
                                                            onChange={(e) => setUserCostInput(e.target.value)}
                                                            className="w-20 px-2 py-1 border border-gray-300 rounded text-xs focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                            placeholder="0.0000"
                                                        />
                                                        <button
                                                            onClick={() => updateAllWorkersForUser(userName)}
                                                            disabled={updating || !userCostInput}
                                                            className="px-2 py-1 bg-blue-600 text-white text-xs rounded hover:bg-blue-700 disabled:opacity-50"
                                                        >
                                                            {updating ? 'Updating...' : 'Update'}
                                                        </button>
                                                    </div>
                                                </td>
                                                <td className="px-3 sm:px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                                                    <button
                                                        onClick={() => toggleUserExpansion(userName)}
                                                        className="text-blue-600 hover:text-blue-800 font-medium text-xs sm:text-sm px-3 py-1 rounded border border-blue-200 hover:border-blue-300 transition-colors"
                                                        disabled={isLoadingWorkers}
                                                    >
                                                        {isLoadingWorkers ? (
                                                            <span className="flex items-center gap-1">
                                                                <div className="animate-spin rounded-full h-3 w-3 border-b-2 border-blue-600"></div>
                                                                Loading...
                                                            </span>
                                                        ) : (isExpanded ? 'Hide Workers' : 'Show Workers')}
                                                    </button>
                                                </td>
                                            </tr>
                                            {isExpanded && (
                                                <tr>
                                                    <td colSpan={4} className="px-0 py-0 bg-gray-50">
                                                        {isLoadingWorkers ? (
                                                            <div className="flex justify-center items-center h-16">
                                                                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                                                                <span className="ml-2 text-sm text-gray-600">Loading workers for {cleanName(userName)}...</span>
                                                            </div>
                                                        ) : workers && workers.length > 0 ? (
                                                            <div className="border-t border-gray-200">
                                                                {/* Selected Workers Update Controls */}
                                                                <div className="bg-green-50 border-b border-gray-200 px-4 py-3">
                                                                    <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center justify-between">
                                                                        <div className="flex flex-col sm:flex-row gap-2 items-start sm:items-center">
                                                                            <span className="text-sm font-medium text-green-900 whitespace-nowrap">
                                                                                Update selected workers ({getSelectedCount(userName)}):
                                                                            </span>
                                                                            <div className="flex gap-2 items-center">
                                                                                <button
                                                                                    onClick={() => selectAllWorkers(userName, workers)}
                                                                                    disabled={updating}
                                                                                    className="px-2 py-1 bg-gray-600 text-white text-xs rounded hover:bg-gray-700 disabled:opacity-50"
                                                                                >
                                                                                    Select All
                                                                                </button>
                                                                                <button
                                                                                    onClick={() => clearSelectedWorkers(userName)}
                                                                                    disabled={updating}
                                                                                    className="px-2 py-1 bg-gray-600 text-white text-xs rounded hover:bg-gray-700 disabled:opacity-50"
                                                                                >
                                                                                    Clear
                                                                                </button>
                                                                                <span className="text-sm">$</span>
                                                                                <input
                                                                                    type="number"
                                                                                    step="0.0001"
                                                                                    min="0"
                                                                                    value={bulkCostInput}
                                                                                    onChange={(e) => setBulkCostInput(e.target.value)}
                                                                                    className="w-24 px-2 py-1 border border-gray-300 rounded text-sm focus:outline-none focus:ring-2 focus:ring-green-500"
                                                                                    placeholder="0.0000"
                                                                                />
                                                                                <button
                                                                                    onClick={() => updateSelectedWorkers(userName)}
                                                                                    disabled={updating || getSelectedCount(userName) === 0 || !bulkCostInput}
                                                                                    className="px-3 py-1 bg-green-600 text-white text-xs rounded hover:bg-green-700 disabled:opacity-50"
                                                                                >
                                                                                    {updating ? 'Updating...' : 'Update Selected'}
                                                                                </button>
                                                                            </div>
                                                                        </div>
                                                                    </div>
                                                                </div>

                                                                {/* Workers Table */}
                                                                <div className="overflow-x-auto">
                                                                    <table className="w-full">
                                                                        <thead className="bg-gray-100">
                                                                            <tr>
                                                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                                                    Select
                                                                                </th>
                                                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                                                    Worker Name
                                                                                </th>
                                                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                                                    Machine
                                                                                </th>
                                                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                                                    Hosting Cost
                                                                                </th>
                                                                                <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                                                                                    Power
                                                                                </th>
                                                                            </tr>
                                                                        </thead>
                                                                        <tbody className="bg-white divide-y divide-gray-200">
                                                                            {workers.map((worker, index) => (
                                                                                <tr key={worker.workerName || index} className="hover:bg-gray-50">
                                                                                    <td className="px-4 py-3">
                                                                                        <input
                                                                                            type="checkbox"
                                                                                            checked={selectedWorkers[userName]?.has(worker.workerName) || false}
                                                                                            onChange={() => {
                                                                                                console.log('Checkbox clicked for:', worker.workerName);
                                                                                                toggleWorkerSelection(userName, worker.workerName);
                                                                                            }}
                                                                                            className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                                                                                        />
                                                                                    </td>
                                                                                    <td className="px-4 py-3 text-sm font-medium text-gray-900">
                                                                                        <div className="truncate max-w-32 sm:max-w-none" title={cleanName(worker.workerName || 'N/A')}>
                                                                                            {cleanName(worker.workerName || 'N/A')}
                                                                                        </div>
                                                                                    </td>
                                                                                    <td className="px-4 py-3 text-sm text-gray-700">
                                                                                        <div className="truncate max-w-32 sm:max-w-none" title={worker.machine || 'N/A'}>
                                                                                            {worker.machine || 'N/A'}
                                                                                        </div>
                                                                                    </td>
                                                                                    <td className="px-4 py-3 text-sm font-bold text-red-600">
                                                                                        ${(worker.hostingCost || 0).toFixed(4)}
                                                                                    </td>
                                                                                    <td className="px-4 py-3 text-sm font-bold text-yellow-600">
                                                                                        {(worker.power || 0).toFixed(2)}W
                                                                                    </td>
                                                                                </tr>
                                                                            ))}
                                                                        </tbody>
                                                                    </table>
                                                                </div>
                                                            </div>
                                                        ) : (
                                                            <div className="text-center py-8">
                                                                <FontAwesomeIcon icon={faCog} className="w-12 h-12 text-gray-300 mb-2" />
                                                                <p className="text-sm text-gray-500">No workers found for this user</p>
                                                            </div>
                                                        )}
                                                    </td>
                                                </tr>
                                            )}
                                        </React.Fragment>
                                    );
                                })}
                            </tbody>
                        </table>
                    </div>
                </div>
            )}

            {/* No Users State */}
            {!loadingUsers && filteredUsers.length === 0 && users.length > 0 && (
                <div className="text-center py-8">
                    <FontAwesomeIcon icon={faUsers} className="w-16 h-16 text-gray-300 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No users found</h3>
                    <p className="text-gray-500">No users match your search criteria.</p>
                </div>
            )}

            {!loadingUsers && users.length === 0 && (
                <div className="text-center py-12">
                    <FontAwesomeIcon icon={faUsers} className="w-16 h-16 text-gray-300 mb-4" />
                    <h3 className="text-lg font-medium text-gray-900 mb-2">No Users Available</h3>
                    <p className="text-gray-500">No users found in the system.</p>
                </div>
            )}
        </div>
    );
};

export default HostingCost;
